import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type {
  AnalysisGroup,
  AnalysisGroupStatus,
  PlanAnalysisResult,
  PlanUnit,
  PlanVignette,
  WorldBbox,
} from "@/utils/plan-analysis/types";
import type { PlanAnalysisAiSuggestion, PlanAnalysisGroupEdit } from "@/types";
import { planAnalysisRepository } from "@/repositories/plan-analysis.repository";
import { Logger } from "@/logger";

/** Étapes du pipeline d'analyse, dans l'ordre d'exécution. */
export const ANALYSIS_STEPS = [
  "scan",
  "views",
  "grouping",
  "photos",
  "ai_grouping",
  "ai_describe",
] as const;
export type AnalysisStep = (typeof ANALYSIS_STEPS)[number];

/**
 * État de l'analyse du plan — groupes détectés, statuts de validation,
 * suggestions IA, groupe courant et filtres de l'onglet Analyse.
 *
 * Le store est la source de vérité UI et persiste chaque mutation en Dexie
 * (table `planAnalyses`, un enregistrement par dessin). Il ne touche JAMAIS
 * au moteur ODA : le scan, le zoom et le stamp passent par
 * `composable/usePlanAnalysis.ts`.
 */
export const usePlanAnalysisState = defineStore("planAnalysisState", () => {
  const drawingId = ref<number | null>(null);
  const groups = ref<AnalysisGroup[]>([]);
  const statuses = ref<Record<string, AnalysisGroupStatus>>({});
  const observationLocalIds = ref<Record<string, string>>({});
  const aiSuggestions = ref<Record<string, PlanAnalysisAiSuggestion>>({});
  /** Retouches utilisateur (nom / commentaire) — l'IA reste intacte à côté. */
  const edits = ref<Record<string, PlanAnalysisGroupEdit>>({});
  const unassignedTextHandles = ref<string[]>([]);
  const unassignedMarkerHandles = ref<string[]>([]);
  const planDiagonal = ref(0);
  const planBbox = ref<WorldBbox | null>(null);
  /** Vignettes détectées lors de la dernière analyse (vues de la feuille). */
  const vignettes = ref<PlanVignette[]>([]);
  const analyzedAt = ref<number | null>(null);
  /**
   * Unité du plan choisie par l'inspecteur, null tant qu'il ne l'a pas
   * définie — persistée par dessin, analyse ou non.
   */
  const unit = ref<PlanUnit | null>(null);
  /** Observations vivantes connues au moment de l'analyse (stamps orphelins tolérés). */
  const liveObservationIds = ref<string[] | null>(null);
  /** Entités estampées rencontrées au scan : exclues / réintégrées. */
  const scanExcluded = ref<{ stamped: number; stale: number } | null>(null);

  // ── État de parcours (non persisté) ─────────────────────────────────────
  const currentGroupId = ref<string | null>(null);
  const statusFilter = ref<AnalysisGroupStatus>("pending");
  /** Cadres des GROUPES détectés (les rectangles numérotés). */
  const framesVisible = ref(true);
  /** Contours des ZONES (vignettes) détectées sur la feuille. */
  const zonesVisible = ref(true);
  const isAnalyzing = ref(false);
  const isAiRunning = ref(false);
  /**
   * Étape en cours du pipeline d'analyse (affichée sur le plan pendant le
   * balayage), avec un détail chiffré déjà traduit. Null hors analyse.
   */
  const analysisStep = ref<AnalysisStep | null>(null);
  /** Détail chiffré par étape, conservé une fois l'étape passée (lisible jusqu'à la fin). */
  const analysisStepDetails = ref<Partial<Record<AnalysisStep, string>>>({});
  /**
   * Mode association (onglet Parties) : partie sélectionnée vers laquelle un
   * tap sur un badge du plan rattache l'observation ; null hors mode. Les
   * parties classent les OBSERVATIONS — l'état vit ici pour être lu par la
   * barre basse et la surcouche du canvas, hors du volet.
   */
  const assignPartId = ref<number | null>(null);
  /** Ouvrage des parties du mode association (pour la barre basse du canvas). */
  const assignDiagnosticId = ref(0);
  /** Zone de sélection armée : le prochain trait sur le plan rattache ses observations. */
  const zoneSelectMode = ref(false);
  /** Ouverture de l'onglet IA demandée ailleurs (fin d'une analyse). */
  const openRequested = ref(false);
  /** L'onglet IA est affiché : les cadres des groupes sont visibles sur le plan. */
  const isPanelOpen = ref(false);
  /**
   * Les handles des groupes sont-ils valides pour le plan OUVERT ? Une
   * analyse rechargée de Dexie porte les handles d'une session passée — ils
   * ne survivent pas aux round-trips de sauvegarde (seul `entityUid` est
   * stable) et doivent être réhydratés avant toute sélection ou validation.
   */
  const handlesRehydrated = ref(false);
  /**
   * `localId` des observations qui existent RÉELLEMENT sur l'ouvrage, tels
   * que le serveur vient de les rendre (non persisté) ; null tant que la
   * liste n'est pas connue (volet jamais ouvert, hors ligne). C'est ce qui
   * rend « validé » calculé : cf. `statusOf`.
   */
  const knownObservationLocalIds = ref<ReadonlySet<string> | null>(null);

  const hasAnalysis = computed(
    () => drawingId.value !== null && analyzedAt.value !== null,
  );

  /**
   * Statut d'un groupe. « Validé » n'est pas un état qu'on entretient : un
   * groupe l'est tant que l'observation qu'il a créée existe encore parmi
   * celles de l'ouvrage. Supprimée — ici, au bureau ou sur un autre poste —
   * le groupe redevient « à valider », sans passe de relecture. Tant que la
   * liste des observations n'est pas connue, le statut enregistré fait foi.
   */
  const statusOf = (groupId: string): AnalysisGroupStatus => {
    const stored = statuses.value[groupId] ?? "pending";
    if (stored !== "validated") return stored;
    const known = knownObservationLocalIds.value;
    if (!known) return stored;
    const localId = observationLocalIds.value[groupId];
    return localId && known.has(localId) ? "validated" : "pending";
  };

  const filteredGroups = computed(() =>
    groups.value.filter((g) => statusOf(g.id) === statusFilter.value),
  );

  const pendingCount = computed(
    () => groups.value.filter((g) => statusOf(g.id) === "pending").length,
  );
  const validatedCount = computed(
    () => groups.value.filter((g) => statusOf(g.id) === "validated").length,
  );
  const ignoredCount = computed(
    () => groups.value.filter((g) => statusOf(g.id) === "ignored").length,
  );

  const currentGroup = computed(
    () => groups.value.find((g) => g.id === currentGroupId.value) ?? null,
  );

  /** N° de badge d'un groupe : son rang dans l'ordre d'analyse (1-based). */
  const groupNumber = (groupId: string): number =>
    groups.value.findIndex((g) => g.id === groupId) + 1;

  /**
   * Partie d'ouvrage enregistrée sur un groupe — du temps où l'onglet Parties
   * classait les groupes. Les parties se choisissent désormais sur les
   * observations : plus rien dans l'outil ne lit ni n'écrit cette valeur
   * (pas plus que `toggleGroupPart`). L'IA ne rattache pas aux parties — la
   * proposition `partId` d'une suggestion persistée est ignorée.
   */
  const partIdOf = (groupId: string): number | null =>
    edits.value[groupId]?.partId ?? null;

  // ── Persistance ──────────────────────────────────────────────────────────

  /**
   * Écrit l'état courant en Dexie — best-effort, jamais bloquant pour l'UI.
   * Sans analyse, seule l'unité du plan justifie un enregistrement : elle se
   * choisit AVANT la première analyse et doit survivre à la fermeture du plan.
   */
  function persist(): void {
    if (drawingId.value === null) return;
    if (analyzedAt.value === null && unit.value === null) return;
    planAnalysisRepository
      .replace({
        drawingId: drawingId.value,
        analyzedAt: analyzedAt.value,
        planDiagonal: planDiagonal.value,
        planBbox: planBbox.value,
        vignettes: vignettes.value,
        groups: groups.value,
        statuses: statuses.value,
        observationLocalIds: observationLocalIds.value,
        aiSuggestions: aiSuggestions.value,
        edits: edits.value,
        unassignedTextHandles: unassignedTextHandles.value,
        unassignedMarkerHandles: unassignedMarkerHandles.value,
        unit: unit.value,
        liveObservationIds: liveObservationIds.value,
        scanExcluded: scanExcluded.value,
      })
      .catch((e) => Logger.warn(`PlanAnalysisState : persist failed (${e})`));
  }

  function reset(): void {
    drawingId.value = null;
    groups.value = [];
    statuses.value = {};
    observationLocalIds.value = {};
    aiSuggestions.value = {};
    edits.value = {};
    unassignedTextHandles.value = [];
    unassignedMarkerHandles.value = [];
    planDiagonal.value = 0;
    planBbox.value = null;
    vignettes.value = [];
    analyzedAt.value = null;
    unit.value = null;
    liveObservationIds.value = null;
    scanExcluded.value = null;
    currentGroupId.value = null;
    statusFilter.value = "pending";
    framesVisible.value = true;
    zonesVisible.value = true;
    isAnalyzing.value = false;
    isAiRunning.value = false;
    analysisStep.value = null;
    analysisStepDetails.value = {};
    assignPartId.value = null;
    zoneSelectMode.value = false;
    openRequested.value = false;
    isPanelOpen.value = false;
    handlesRehydrated.value = false;
    knownObservationLocalIds.value = null;
  }

  /**
   * Efface TOUTE l'analyse du plan ouvert (groupes, statuts, suggestions,
   * vignettes) et sa persistance Dexie — repartir de zéro naturellement.
   * Les observations déjà validées restent : ce sont de vraies données,
   * leurs entités estampées ne reviendront pas dans un futur scan. L'unité
   * du plan est gardée en mémoire : c'est une propriété du plan, pas de
   * l'analyse.
   */
  async function clearAnalysis(): Promise<void> {
    const id = drawingId.value;
    groups.value = [];
    statuses.value = {};
    observationLocalIds.value = {};
    aiSuggestions.value = {};
    edits.value = {};
    unassignedTextHandles.value = [];
    unassignedMarkerHandles.value = [];
    planDiagonal.value = 0;
    planBbox.value = null;
    vignettes.value = [];
    analyzedAt.value = null;
    currentGroupId.value = null;
    statusFilter.value = "pending";
    handlesRehydrated.value = false;
    if (id === null) return;
    try {
      await planAnalysisRepository.deleteByDrawingId(id);
    } catch (e) {
      Logger.warn(`PlanAnalysisState : clear failed (${e})`);
    }
    // L'enregistrement supprimé portait aussi l'unité : on la réécrit seule.
    persist();
  }

  /** Recharge l'analyse persistée du dessin (à l'ouverture du canvas). */
  async function loadForDrawing(id: number): Promise<void> {
    reset();
    drawingId.value = id;
    try {
      const saved = await planAnalysisRepository.getByDrawingId(id);
      if (!saved) return;
      // Analyses persistées avant le champ `kind` : désordre par défaut.
      groups.value = saved.groups.map((g) => ({
        ...g,
        kind: g.kind ?? "disorder",
      }));
      statuses.value = saved.statuses;
      observationLocalIds.value = saved.observationLocalIds;
      aiSuggestions.value = saved.aiSuggestions;
      edits.value = saved.edits ?? {};
      unassignedTextHandles.value = saved.unassignedTextHandles;
      unassignedMarkerHandles.value = saved.unassignedMarkerHandles;
      planDiagonal.value = saved.planDiagonal;
      planBbox.value = saved.planBbox;
      // Analyses persistées avant la détection des vignettes : aucune.
      vignettes.value = saved.vignettes ?? [];
      unit.value = saved.unit ?? null;
      liveObservationIds.value = saved.liveObservationIds ?? null;
      scanExcluded.value = saved.scanExcluded ?? null;
      analyzedAt.value = saved.analyzedAt;
    } catch (e) {
      Logger.warn(`PlanAnalysisState : load failed (${e})`);
    }
  }

  // ── Mutations ────────────────────────────────────────────────────────────

  /**
   * Installe un (nouveau) résultat d'analyse. Une relance remplace les
   * groupes en attente ; un groupe recomposé à l'identique (même id) garde
   * son statut « ignoré ». Les groupes validés ne réapparaissent pas : leurs
   * entités, estampées, sortent du scan.
   */
  function setAnalysisResult(
    result: PlanAnalysisResult,
    diagonal: number,
    bbox: WorldBbox | null,
    detectedVignettes: PlanVignette[] = [],
  ): void {
    const previous = statuses.value;
    const nextStatuses: Record<string, AnalysisGroupStatus> = {};
    for (const group of result.groups) {
      nextStatuses[group.id] =
        previous[group.id] === "ignored" ? "ignored" : "pending";
    }
    groups.value = result.groups;
    statuses.value = nextStatuses;
    // Les suggestions IA décrivent l'ancien découpage — on repart à zéro.
    aiSuggestions.value = {};
    edits.value = Object.fromEntries(
      Object.entries(edits.value).filter(([id]) => id in nextStatuses),
    );
    observationLocalIds.value = {};
    unassignedTextHandles.value = result.unassignedTextHandles;
    unassignedMarkerHandles.value = result.unassignedMarkerHandles;
    planDiagonal.value = diagonal;
    planBbox.value = bbox;
    vignettes.value = detectedVignettes;
    analyzedAt.value = Date.now();
    // Résultat tout juste calculé sur le plan ouvert : handles frais.
    handlesRehydrated.value = true;
    statusFilter.value = "pending";
    currentGroupId.value =
      result.groups.find((g) => nextStatuses[g.id] === "pending")?.id ?? null;
    persist();
  }

  function setCurrentGroup(groupId: string | null): void {
    currentGroupId.value = groupId;
  }

  /** Unité du plan choisie par l'inspecteur (persistée par dessin). */
  function setUnit(value: PlanUnit | null): void {
    unit.value = value;
    persist();
  }

  /**
   * Observations existant réellement sur l'ouvrage (liste rendue par le
   * serveur), ou null si elle n'est pas connue — cf. `statusOf`.
   */
  function setKnownObservations(localIds: string[] | null): void {
    knownObservationLocalIds.value = localIds ? new Set(localIds) : null;
  }

  /** Contexte du scan de la dernière analyse (observations vivantes, exclusions). */
  function setScanContext(
    ids: string[] | null,
    excluded: { stamped: number; stale: number } | null,
  ): void {
    liveObservationIds.value = ids;
    scanExcluded.value = excluded;
    persist();
  }

  /** Entre (ou sort, avec null) du mode association vers une partie. */
  function setAssignPart(partId: number | null, diagnosticId = 0): void {
    assignPartId.value = partId;
    if (partId === null) zoneSelectMode.value = false;
    else assignDiagnosticId.value = diagnosticId;
  }

  /**
   * Rattache un groupe à une partie, ou l'en détache s'il y était déjà
   * (tap sur le même badge). Retouche persistée avec les autres (`edits`).
   */
  function toggleGroupPart(groupId: string, partId: number): void {
    setGroupEdit(groupId, {
      partId: partIdOf(groupId) === partId ? null : partId,
    });
  }

  /**
   * Étape du pipeline affichée pendant l'analyse (null = terminé, le journal
   * est vidé). Un détail reste affiché sur sa ligne après l'étape.
   */
  function setAnalysisStep(step: AnalysisStep | null, detail?: string): void {
    analysisStep.value = step;
    if (step === null) analysisStepDetails.value = {};
    else if (detail !== undefined) {
      analysisStepDetails.value = { ...analysisStepDetails.value, [step]: detail };
    }
  }

  /** Avance (ou recule) dans les groupes encore à valider. */
  function stepCurrent(step: 1 | -1): void {
    const list = groups.value.filter((g) => statusOf(g.id) === "pending");
    if (list.length === 0) return;
    const index = list.findIndex((g) => g.id === currentGroupId.value);
    const next =
      index === -1
        ? 0
        : (index + step + list.length) % list.length;
    currentGroupId.value = list[next].id;
  }

  function validateGroup(groupId: string, observationLocalId: string): void {
    statuses.value = { ...statuses.value, [groupId]: "validated" };
    observationLocalIds.value = {
      ...observationLocalIds.value,
      [groupId]: observationLocalId,
    };
    // L'observation vient d'être créée : elle existe, sans attendre que la
    // liste du serveur soit relue (le groupe clignoterait « à valider »).
    if (knownObservationLocalIds.value) {
      knownObservationLocalIds.value = new Set([
        ...knownObservationLocalIds.value,
        observationLocalId,
      ]);
    }
    advancePastCurrent(groupId);
    persist();
  }

  function ignoreGroup(groupId: string): void {
    statuses.value = { ...statuses.value, [groupId]: "ignored" };
    advancePastCurrent(groupId);
    persist();
  }

  /** Repasse un groupe ignoré (ou validé à tort côté UI) en attente. */
  function restoreGroup(groupId: string): void {
    statuses.value = { ...statuses.value, [groupId]: "pending" };
    persist();
  }

  /**
   * Après valider/ignorer : le courant passe au prochain « à valider » QUI
   * SUIT dans la liste (14 validé → 15, ou 16 si 15 l'était déjà) ; à
   * défaut, le premier encore en attente avant lui.
   */
  function advancePastCurrent(groupId: string): void {
    if (currentGroupId.value !== groupId) return;
    const list = groups.value;
    const index = list.findIndex((g) => g.id === groupId);
    const isPending = (g: AnalysisGroup): boolean =>
      statusOf(g.id) === "pending";
    const next =
      list.slice(index + 1).find(isPending) ??
      list.slice(0, Math.max(index, 0)).find(isPending);
    currentGroupId.value = next?.id ?? null;
  }

  /**
   * Remplace un groupe corrigé à la main (ajout/retrait de sélection).
   * L'id change avec les membres : statut, suggestion et groupe courant
   * suivent le nouvel id.
   */
  function replaceGroup(oldId: string, group: AnalysisGroup): void {
    const index = groups.value.findIndex((g) => g.id === oldId);
    if (index === -1) return;
    const nextGroups = [...groups.value];
    nextGroups[index] = group;
    groups.value = nextGroups;
    if (oldId !== group.id) {
      const { [oldId]: oldStatus, ...restStatuses } = statuses.value;
      statuses.value = { ...restStatuses, [group.id]: oldStatus ?? "pending" };
      const { [oldId]: oldSuggestion, ...restSuggestions } = aiSuggestions.value;
      aiSuggestions.value = oldSuggestion
        ? { ...restSuggestions, [group.id]: oldSuggestion }
        : restSuggestions;
      const { [oldId]: oldEdit, ...restEdits } = edits.value;
      edits.value = oldEdit ? { ...restEdits, [group.id]: oldEdit } : restEdits;
      // Le lien vers l'observation créée suit aussi : « validé » se calcule
      // dessus (cf. `statusOf`), un groupe validé recomposé resterait sinon
      // « à valider » alors que son observation existe.
      const { [oldId]: oldLocalId, ...restLocalIds } = observationLocalIds.value;
      observationLocalIds.value = oldLocalId
        ? { ...restLocalIds, [group.id]: oldLocalId }
        : restLocalIds;
      if (currentGroupId.value === oldId) currentGroupId.value = group.id;
    }
    persist();
  }

  /** Ajoute un groupe créé à la main (en fin de liste) et le rend courant. */
  function addGroup(group: AnalysisGroup): void {
    groups.value = [...groups.value.filter((g) => g.id !== group.id), group];
    statuses.value = { ...statuses.value, [group.id]: "pending" };
    currentGroupId.value = group.id;
    persist();
  }

  /** Retire un groupe vidé par une correction (plus aucune entité). */
  function removeGroup(groupId: string): void {
    groups.value = groups.value.filter((g) => g.id !== groupId);
    const { [groupId]: _s, ...restStatuses } = statuses.value;
    statuses.value = restStatuses;
    const { [groupId]: _a, ...restSuggestions } = aiSuggestions.value;
    aiSuggestions.value = restSuggestions;
    const { [groupId]: _e, ...restEdits } = edits.value;
    edits.value = restEdits;
    if (currentGroupId.value === groupId) {
      currentGroupId.value =
        groups.value.find((g) => statusOf(g.id) === "pending")?.id ?? null;
    }
    persist();
  }

  /**
   * Applique une fusion (proposée par l'IA) : les groupes `ids` sont
   * remplacés par `merged`, inséré au rang du premier d'entre eux.
   */
  function mergeGroups(ids: string[], merged: AnalysisGroup): void {
    const firstIndex = groups.value.findIndex((g) => ids.includes(g.id));
    if (firstIndex === -1) return;
    const nextGroups = groups.value.filter((g) => !ids.includes(g.id));
    nextGroups.splice(firstIndex, 0, merged);
    groups.value = nextGroups;
    const nextStatuses = { ...statuses.value };
    for (const id of ids) delete nextStatuses[id];
    nextStatuses[merged.id] = "pending";
    statuses.value = nextStatuses;
    if (ids.includes(currentGroupId.value ?? "")) {
      currentGroupId.value = merged.id;
    }
    persist();
  }

  /**
   * Retouche utilisateur d'un groupe (nom / commentaire / type). Une valeur
   * `undefined` dans le patch efface la retouche (retour à la proposition
   * IA, ou à la nature dérivée) ; une entrée vide disparaît.
   */
  function setGroupEdit(groupId: string, patch: PlanAnalysisGroupEdit): void {
    const current = { ...(edits.value[groupId] ?? {}), ...patch };
    for (const key of Object.keys(current) as Array<keyof PlanAnalysisGroupEdit>) {
      if (current[key] === undefined) delete current[key];
    }
    const next = { ...edits.value };
    if (Object.keys(current).length === 0) delete next[groupId];
    else next[groupId] = current;
    edits.value = next;
    persist();
  }

  /** Fusionne des suggestions describe (régénération d'un seul groupe). */
  function mergeAiSuggestions(
    suggestions: Record<string, PlanAnalysisAiSuggestion>,
  ): void {
    aiSuggestions.value = { ...aiSuggestions.value, ...suggestions };
    persist();
  }

  /** Installe les suggestions de la passe IA (clés = ids de groupes actuels). */
  function setAiSuggestions(
    suggestions: Record<string, PlanAnalysisAiSuggestion>,
  ): void {
    aiSuggestions.value = suggestions;
    persist();
  }

  /**
   * Réhydrate les handles de tous les groupes depuis l'index uid → handle du
   * plan ouvert. Une entité disparue est retirée du groupe ; un groupe vidé
   * est conservé (bbox intacte) mais ne sélectionne plus rien.
   */
  function rehydrateHandles(uidToHandle: Map<string, string>): void {
    groups.value = groups.value.map((group) => {
      const handles = group.entityUids
        .map((uid) => uidToHandle.get(uid))
        .filter((h): h is string => !!h);
      // Les markerHandles d'une autre session sont irrécupérables (pas de
      // correspondance uid↔rôle persistée) : on les restreint aux vivants.
      const alive = new Set(handles);
      return {
        ...group,
        handles,
        markerHandles: group.markerHandles.filter((h) => alive.has(h)),
      };
    });
    handlesRehydrated.value = true;
    persist();
  }

  /** Ouvre l'onglet IA du volet Observations depuis un autre coin du canvas. */
  function requestOpen(): void {
    openRequested.value = true;
  }
  function consumeOpenRequest(): void {
    openRequested.value = false;
  }

  return {
    // état
    drawingId,
    groups,
    statuses,
    observationLocalIds,
    aiSuggestions,
    edits,
    unassignedTextHandles,
    unassignedMarkerHandles,
    planDiagonal,
    planBbox,
    vignettes,
    analyzedAt,
    unit,
    liveObservationIds,
    scanExcluded,
    currentGroupId,
    statusFilter,
    framesVisible,
    zonesVisible,
    isAnalyzing,
    isAiRunning,
    analysisStep,
    analysisStepDetails,
    assignPartId,
    assignDiagnosticId,
    zoneSelectMode,
    openRequested,
    isPanelOpen,
    handlesRehydrated,
    knownObservationLocalIds,
    // dérivés
    hasAnalysis,
    filteredGroups,
    pendingCount,
    validatedCount,
    ignoredCount,
    currentGroup,
    statusOf,
    groupNumber,
    partIdOf,
    // actions
    loadForDrawing,
    reset,
    clearAnalysis,
    setAnalysisResult,
    setCurrentGroup,
    setUnit,
    setKnownObservations,
    setScanContext,
    setAnalysisStep,
    setAssignPart,
    toggleGroupPart,
    stepCurrent,
    validateGroup,
    ignoreGroup,
    restoreGroup,
    replaceGroup,
    addGroup,
    removeGroup,
    mergeGroups,
    setGroupEdit,
    rehydrateHandles,
    setAiSuggestions,
    mergeAiSuggestions,
    requestOpen,
    consumeOpenRequest,
  };
});
